import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShoppingBag } from "lucide-react";
import { CONTACT } from "../data/menu";

const HERO_IMG = "/photos/hero-spread.jpg";

const LINES = ["Fish & chips,", "chicken", "& Greek", "souvláki."];

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] } },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-white pt-28 lg:pt-28 lg:pb-16"
    >
      {/* faint oversized watermark */}
      <span
        aria-hidden
        className="pointer-events-none select-none absolute -bottom-[6vw] -left-[1vw] font-display italic text-[24vw] leading-none text-[var(--ice)] z-0"
      >
        est.
      </span>

      <div className="relative z-10 max-w-[1500px] mx-auto px-6 lg:px-12 lg:min-h-[86vh] grid lg:grid-cols-[1.05fr_0.95fr] items-center gap-10">
        {/* Left — kinetic type */}
        <motion.div style={{ y: textY }} className="order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-12 bg-[var(--azure)]" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--slate)]">
              Est. 2025 · Donvale · 65 Mitcham Rd
            </span>
          </motion.div>

          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate="show"
            className="font-display font-black text-[var(--navy)] leading-[0.92] tracking-[-0.02em]"
            style={{ fontSize: "clamp(2.75rem, 7vw, 5.75rem)" }}
          >
            {LINES.map((line, i) => (
              <span key={i} className="reveal-mask">
                <motion.span
                  variants={lineChild}
                  className="block"
                  style={i === 3 ? { fontStyle: "italic", color: "var(--azure)" } : undefined}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <p className="mt-8 max-w-md text-[var(--slate)] text-base lg:text-lg leading-relaxed">
            Run by a proud Greek family, Lucky Corner is Donvale's
            neighbourhood chippy — clean, fresh seafood (never frozen), golden
            chips, and a little Greek soul in every serve.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={CONTACT.orderUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-order-btn"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[13px] font-mono tracking-[0.15em] uppercase text-white transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              style={{ background: "var(--azure)" }}
            >
              <ShoppingBag size={15} strokeWidth={2.4} />
              Online Order
            </a>
            <a
              href="#menu"
              data-testid="hero-menu-btn"
              className="rounded-full px-7 py-3.5 text-white text-[13px] font-mono tracking-[0.15em] uppercase transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              style={{ background: "var(--navy)" }}
            >
              Browse the Menu
            </a>
            <a
              href={CONTACT.phoneHref}
              data-testid="hero-call-btn"
              className="rounded-full px-7 py-3.5 text-[13px] font-mono tracking-[0.15em] uppercase border border-[var(--hair)] text-[var(--navy)] transition-colors duration-300 hover:bg-[var(--ice)]"
            >
              Call {CONTACT.phone}
            </a>
          </div>
        </motion.div>

        {/* Right — floating clipped dish */}
        <motion.div style={{ y: imgY }} className="order-1 lg:order-2 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
            className="relative aspect-[4/5] w-full max-w-[440px] mx-auto rounded-[2px] overflow-hidden"
            style={{ boxShadow: "0 40px 90px -40px rgba(0,42,84,0.55)" }}
          >
            <motion.img
              src={HERO_IMG}
              alt="Lucky Corner fish & chips, chicken and souvlaki spread"
              style={{ scale: imgScale }}
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0 mix-blend-multiply opacity-25"
              style={{ background: "radial-gradient(circle at 50% 30%, transparent 40%, var(--navy) 130%)" }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.3, duration: 0.7 }}
            className="absolute -left-3 lg:-left-8 top-8 rotate-[-6deg] bg-white rounded-full px-5 py-4 text-center"
            style={{ boxShadow: "0 20px 50px -20px rgba(0,42,84,0.4)", border: "1px solid var(--hair)" }}
          >
            <span className="block font-display italic text-[var(--azure)] text-2xl leading-none">Never</span>
            <span className="block font-mono text-[9px] tracking-[0.22em] uppercase text-[var(--navy)] mt-1">Frozen</span>
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#story"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[var(--slate)] z-10"
      >
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.a>
    </section>
  );
};
